<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { CheckboxGroupRoot } from 'reka-ui'
import { faMinus, faPlus } from '@fortawesome/duotone-regular-svg-icons'
import AuCheckbox from '~/components/ui/AuCheckbox.vue'
import AggregationButton from '~/components/search/AggregationButton.vue'

export interface AggregationEntryItem {
  group: string
  key: string[]
}

const props = defineProps<{
  title: string
  isRefetching: boolean
  aggregations: AggregationEntryItem[]
  criterions: string[]
  translator: (entry: AggregationEntryItem) => string
  openOnMount?: boolean
}>()

const emit = defineEmits(['toggleCriterion'])

const open = ref(false)

const activeEntries = computed(() => {
  return props.aggregations.filter(entry =>
    entry.key.every(key => props.criterions.includes(key)),
  )
})

const handleUpdate = (nextEntries: AggregationEntryItem[]) => {
  const isAdding = nextEntries.length > activeEntries.value.length
  const target = isAdding
    ? nextEntries.find(n => !activeEntries.value.some(a => a.group === n.group))
    : activeEntries.value.find(a => !nextEntries.some(n => n.group === a.group))

  if (target) {
    emit('toggleCriterion', target.key)
  }
}

onMounted(() => {
  if (props.criterions.length || props.openOnMount) {
    open.value = true
  }
})
</script>

<template>
  <div class="rounded-xl bg-white">
    <button
      class="group flex w-full cursor-pointer items-center justify-between rounded px-4 py-3 focus-visible:ring"
      type="button"
      @click="open = !open"
    >
      <span
        class="font-bold underline-offset-4 group-hover:underline"
        v-text="title"
      />
      <font-awesome-icon
        :icon="open ? faMinus : faPlus"
        :spin="open"
        style="--fa-animation-duration: 200ms; --fa-animation-iteration-count: 1;"
      />
    </button>
    <Transition
      :duration="500"
      name="nested"
    >
      <div
        v-show="open"
        class="outer border-t-gray-10 mx-4 border-t py-3"
      >
        <CheckboxGroupRoot
          :model-value="activeEntries"
          class="inner flex flex-col gap-2"
          @update:model-value="handleUpdate"
        >
          <AggregationButton
            v-for="entry in aggregations"
            :key="entry.group"
            :disabled="isRefetching"
            :entry="entry"
            :name="translator(entry)"
            full-width
            type="label"
          >
            <AuCheckbox :value="entry" />
          </AggregationButton>
        </CheckboxGroupRoot>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.nested-enter-active,
.nested-leave-active {
  transition: all 0.3s linear;
  max-height: 500px;
}

.nested-leave-active {
  transition-delay: 0.2s;
}

.nested-enter-from,
.nested-leave-to {
  transform: translateY(-5px);
  opacity: 0;
  max-height: 0;
}

.nested-enter-active .inner,
.nested-leave-active .inner {
  transition:
    transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
  will-change: transform, opacity;
}

.nested-enter-active .inner {
  transition-delay: 0.2s;
}

.nested-enter-from .inner,
.nested-leave-to .inner {
  transform: translateY(-5px);
  opacity: 0.001;
}
</style>
